import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faTrashCan, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { useAppStore } from '../../store/useAppStore';
import { useSettingsStore } from '../../store/useSettingsStore';
import { useBakingTimer } from '../../hooks/useBakingTimer';
import { useCustomerOrders } from '../../hooks/useCustomerOrders';
import { useMutationFeedback } from '../../hooks/useMutationFeedback';
import { isCustomerOrder, canDeleteOrder, getFlatToppings } from '../../utils/orderUtils';
import { containsOutOfStock, isOutOfStock, makeStableSuggestionCache } from '../../utils/stockUtils';
import { formatDuration } from '../../utils/bakingTime';
import Badge from '../ui/Badge';
import IconButton from '../ui/IconButton';
import ConfirmDialog from '../ConfirmDialog';

function getOrderStateClasses(order) {
  if (order.state === 'done') return 'border-emerald-500/50 bg-emerald-500/8';
  if (order.state === 'baking') return 'border-orange-500/50 bg-orange-500/8';
  return 'border-[color:var(--app-border)] bg-[color:var(--app-surface)]';
}

// Out-of-stock toppings render strike-through in the order cards.
function ToppingText({ toppings, config, className = '' }) {
  return (
    <span className={className}>
      {toppings.map((topping, index) => (
        <span key={topping}>
          {index > 0 ? ', ' : ''}
          <span className={isOutOfStock(config, topping) ? 'line-through opacity-60' : ''}>{topping}</span>
        </span>
      ))}
    </span>
  );
}

function CustomerOrderCard({ order }) {
  const { t } = useTranslation();
  const currentCustomer = useSettingsStore((state) => state.currentCustomer);
  const config = useAppStore((state) => state.config);
  const deleteOrder = useAppStore((state) => state.deleteOrder);
  const editOrder = useAppStore((state) => state.editOrder);
  const startEditOrder = useAppStore((state) => state.startEditOrder);
  const setMode = useSettingsStore((state) => state.setMode);
  const customerOrders = useCustomerOrders();
  const { run } = useMutationFeedback();
  const timer = useBakingTimer(order);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Suggestion chips are rolled once per card mount (page visit) and frozen.
  const stableSuggestions = useRef(makeStableSuggestionCache()).current;

  const isOwn = isCustomerOrder(order, currentCustomer);
  const remaining = order.state === 'baking' ? timer.remaining : null;
  const canEdit = isOwn && canDeleteOrder(order);
  const canDelete = isOwn && canDeleteOrder(order);
  const ownQueuedCount = customerOrders.filter(canDeleteOrder).length;
  const awaitingStock = isOwn && order.state === 'queued' && order.awaitingCustomerAck;
  const stockToppings = awaitingStock ? containsOutOfStock(order, config) : [];

  const handleEdit = () => {
    startEditOrder(order.id);
    setMode('menu');
  };

  const handleDelete = async () => {
    setConfirmOpen(false);
    await run(() => deleteOrder(order.id));
  };

  const removeTopping = async (topping) => {
    await run(
      () => editOrder(order.id, {
        toppings: order.toppings.filter((item) => item !== topping),
        customToppings: order.customToppings || [],
        comment: order.comment || '',
        offer: null,
      }),
      t('msg.orderEdited'),
    );
  };

  const acceptSuggestion = async (topping, alternative) => {
    await run(
      () => editOrder(order.id, {
        toppings: order.toppings.map((item) => (item === topping ? alternative : item)),
        customToppings: order.customToppings || [],
        comment: order.comment || '',
      }),
      t('msg.orderEdited'),
    );
  };

  const details = ownQueuedCount > 1 ? (
    <div className="space-y-1">
      {order.offer && (
        <p className="text-sm">
          <span className="theme-muted">{t('ts.offer')}</span> {order.offer}
        </p>
      )}
      <p className="text-sm">
        <span className="theme-muted">{t('ts.toppings')}</span>{' '}
        {getFlatToppings(order, config).length > 0 ? (
          <ToppingText toppings={getFlatToppings(order, config)} config={config} />
        ) : (
          t('cust.noToppings')
        )}
      </p>
    </div>
  ) : null;

  return (
    <div className={`rounded-lg border px-4 py-3 ${getOrderStateClasses(order)} ${isOwn ? 'ring-1 ring-emerald-500/40' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-semibold">
            {order.orderNo ? `#${order.orderNo} ` : ''}{order.customerName}
          </p>
          {isOwn && (
            <Badge toneClass="border-emerald-500/40 bg-emerald-500/15 theme-accent">
              {t('cust.you')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <IconButton
              icon={faPenToSquare}
              label={t('edit.label')}
              onClick={handleEdit}
            />
          )}
          {canDelete && (
            <IconButton
              icon={faTrashCan}
              label={t('card.delete')}
              tone="red"
              onClick={() => setConfirmOpen(true)}
            />
          )}
          <span className="rounded border border-[color:var(--app-border)] bg-[color:var(--app-surface)] px-2 py-0.5 text-xs font-medium uppercase tracking-wide">
            {t(`state.${order.state}`)}
          </span>
        </div>
      </div>

      <p className="mt-0.5 text-xs theme-muted">{new Date(order.createdAt).toLocaleString()}</p>
      <p className="mt-1.5 text-sm">
        {getFlatToppings(order, config).length > 0 ? (
          <ToppingText toppings={getFlatToppings(order, config)} config={config} />
        ) : (
          t('cust.noToppings')
        )}
      </p>

      {awaitingStock && stockToppings.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <p className="text-sm font-semibold theme-accent-warm">{t('stock.verifyOrder')}</p>
          <div className="mt-2 space-y-1.5">
            {stockToppings.map((topping) => {
              const alternatives = stableSuggestions(topping, config, order.toppings || []);
              return (
                <div key={topping} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="line-through opacity-70">{topping}</span>
                  <button
                    type="button"
                    onClick={() => removeTopping(topping)}
                    className="rounded-md border border-[color:var(--app-border)] px-2.5 py-1 text-xs font-semibold theme-muted transition hover:opacity-80"
                  >
                    {t('stock.orderWithout', { topping })}
                  </button>
                  {alternatives.length > 0 ? (
                    <>
                      <span className="theme-muted">{t('stock.suggest')}</span>
                      {alternatives.map((alternative) => (
                        <button
                          key={alternative}
                          type="button"
                          onClick={() => acceptSuggestion(topping, alternative)}
                          className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold theme-accent transition hover:bg-emerald-500/20"
                        >
                          {alternative}
                        </button>
                      ))}
                    </>
                  ) : (
                    <span className="theme-muted">{t('stock.noAlternative')}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2.5">
            <button
              type="button"
              onClick={handleEdit}
              className="rounded-md border border-[color:var(--app-border)] px-2.5 py-1 text-xs font-semibold theme-muted transition hover:opacity-80"
            >
              {t('stock.modifyOrder')}
            </button>
          </div>
        </div>
      )}

      {order.half && (
        <Badge toneClass="border-amber-500/40 bg-amber-500/10 theme-accent-warm" className="mt-1.5 inline-block">
          {t('cust.halfPizza')}
        </Badge>
      )}

      {order.state === 'baking' && remaining !== null && (
        remaining > 0 ? (
          <p className="mt-2 text-sm font-medium theme-accent-warm">
            {t('cust.readyIn')} {formatDuration(remaining)}
          </p>
        ) : (
          <p className="mt-2 text-sm font-medium theme-accent-warm">{t('cust.almostDone')}</p>
        )
      )}

      <ConfirmDialog
        open={confirmOpen}
        message={t('confirm.removeMessage')}
        details={details}
        confirmLabel={t('confirm.remove')}
        cancelLabel={t('confirm.keep')}
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}

export default CustomerOrderCard;
